/**
 * THE LAST BOARD YOU LOOKED AT, KEPT ON THIS MACHINE.
 *
 * WHY THIS EXISTS. Vinnie, 23 August 2026: *"When going back to the board from
 * another window, it still says loading the board... no other site, amazon,
 * apple, google, has to reload when you go back to that tab. For a SaaS this is
 * highly concerning... no one has time to wait for that during a work day."*
 *
 * WHAT IS ACTUALLY HAPPENING, because it is not what it looks like. Switching
 * tabs does not reload anything — measured, ninety seconds away and the board
 * never blinks. What reloads is a window the BROWSER threw away: Chrome and Edge
 * discard tabs that have been in the background to save memory, and coming back
 * to one runs the whole app from nothing. That is a real cold start, and it took
 * 2,168ms with "Loading the board…" on screen for 1,981 of them.
 *
 * SO THE FETCH CANNOT BE AVOIDED — THE BLANK SCREEN CAN. The data has to be
 * asked for again; what nobody has to do is stare at nothing while it arrives.
 * Paint the board that was there a moment ago, and let the live one replace it
 * when it lands. This is what every app he named does.
 *
 * ONE SLOT, NOT A LIBRARY. Only the board being looked at is worth keeping. A
 * cache per board would grow without limit across a working year and would be
 * mostly boards nobody is going back to.
 *
 * WHOSE IT IS, IS PART OF THE KEY. A snapshot is only handed back to the same
 * person on the same board. Getting this wrong would show one customer another's
 * work on a shared machine, which is worse than any delay it saves — hence
 * `forget()` on signing out and on being refused a board, and the checks in
 * `recall()` rather than trust that the slot must be ours.
 *
 * IT IS A PICTURE, NOT A SOURCE OF TRUTH. Nothing is ever written from here, and
 * it is replaced within a second or two by the real thing. If somebody's access
 * was taken away while they were gone, they see the old board for about a
 * second and then the refusal — the same second they would have spent watching
 * "Loading the board…", and the rules still decide every read and every write.
 */
import type { Build, Doc, Issue, Signoff } from './types';
import type { Project } from './projects';
import type { Sprint } from './sprints';

const SLOT = 'flightdeck.last-board.v1';

/**
 * A WEEK, AFTER WHICH IT IS THROWN AWAY UNREAD.
 *
 * Not about correctness — a snapshot is replaced whatever its age. It is about
 * not painting a board from a fortnight's holiday ago, where the flash of a
 * genuinely old board reads as "the app has lost my work" rather than as speed.
 */
const KEEP_FOR = 7 * 24 * 60 * 60 * 1000;

/**
 * ABOUT A MEGABYTE OF TEXT. Browsers give an origin roughly five, shared with
 * everything else the app keeps — the board choice, the theme, the session. A
 * board big enough to blow that is exactly the board this is for, so it stops
 * rather than throwing and losing the smaller things stored beside it.
 */
const TOO_BIG = 1_000_000;

export interface BoardSnapshot {
  who: string;
  project: string;
  at: number;
  issues: Issue[];
  builds: Build[];
  signoffs: Signoff[];
  documents: Doc[];
  sprints: Sprint[];
  /*
   * THE BOARDS THIS PERSON CAN OPEN — kept from 25 August 2026, and kept
   * deliberately rather than because everything else was.
   *
   * WHY IT HAS TO BE HERE. The cards were kept and the list of boards was not,
   * so on every return the app held a board's contents and no board to put them
   * in. The screen that asks a newcomer to set up is the one that answers an
   * empty list, and it was shown to people who had set up years ago — then,
   * when that was stopped, the board was drawn with nothing to draw and looped
   * until React gave up, which is a white page and no message at all.
   *
   * WHY THIS ONE AND NOT THE PERSON'S OWN DETAILS. An empty list of boards
   * asks somebody to make one. An empty set of details would say they never
   * agreed to the terms and never chose a password — and answering either of
   * those out of a file the person can edit is not a cache, it is a way past
   * them. The details are asked of the database every time and always will be.
   *
   * A NAME IS ALL THIS CAN LEAK, and only to somebody already holding this
   * browser: recall() hands a snapshot back only to the address that wrote it,
   * and every card, every setting and every action behind these names is
   * refused or allowed by the rules in supabase/migrations, which have never
   * once asked this file anything.
   */
  projects: Project[];
}

/*
 * EVERY CALL GUARDED, because storage is not always there to be written to.
 * Private windows, a full disk and a browser configured to refuse it all throw
 * on the ordinary path rather than returning false, and none of them is a reason
 * for the app not to open.
 */
const safely = <T>(f: () => T, fallback: T): T => {
  try {
    return f();
  } catch {
    return fallback;
  }
};

let pending: ReturnType<typeof setTimeout> | undefined;

/**
 * Keep this snapshot, but not more than once every couple of seconds.
 *
 * WHY IT WAITS. A board arriving sets five collections in quick succession, and
 * a busy afternoon changes one of them constantly. Writing on each would turn a
 * quarter-megabyte of JSON into a synchronous, main-thread cost on every
 * keystroke somebody else makes — a delay added by the thing meant to remove
 * one.
 */
export const remember = (snapshot: BoardSnapshot): void => {
  clearTimeout(pending);
  pending = setTimeout(() => {
    safely(() => {
      const text = JSON.stringify(snapshot);
      if (text.length > TOO_BIG) return;
      localStorage.setItem(SLOT, text);
    }, undefined);
  }, 2000);
};

/**
 * The snapshot for this person on this board, or nothing.
 *
 * NOTHING IS THE ORDINARY ANSWER and every branch here returns it: no slot, a
 * different person, a different board, too old, or text that will not parse
 * because the shape changed in a release. A cache that cannot be read is a cache
 * that is not used, never an app that will not start.
 */
export const recall = (who: string, project: string): BoardSnapshot | null =>
  safely(() => {
    if (!who || !project) return null;
    const text = localStorage.getItem(SLOT);
    if (!text) return null;
    const snapshot = JSON.parse(text) as BoardSnapshot;
    if (snapshot.who !== who || snapshot.project !== project) return null;
    if (!Array.isArray(snapshot.issues)) return null;
    if (!(Date.now() - snapshot.at < KEEP_FOR)) return null;
    return snapshot;
  }, null);

/** Signing out, or being refused. Both mean this must not be handed back. */
export const forget = (): void => {
  clearTimeout(pending);
  safely(() => localStorage.removeItem(SLOT), undefined);
};
